/**
 * @file src/api/trackUserSettingsService.js
 * @description REST helpers for private per-user track settings.
 */
import { isLocalEntryId } from '../local/localAudioIds.js';
import { fetchJsonFromApi } from './httpClient.js';
import { requestEmbeddedAuthToken } from './dataManager/loaders.js';
import { resolveViewerAuth, viewerCacheKey } from '../auth/viewerAuth.js';
import { ensureLoginPrompt, getLoginHref } from '../ui/loginPrompt.js';
import {
  READ_OUTCOME,
  configurationEpoch,
  isConfigurationEpochCurrent,
} from '../config/configurationPack.js';

const BASE_ENDPOINT = '/me/users/configurations/track-settings';
const MISSING_TRACK_SETTINGS_PREFIX = 'orbiters:missing-track-user-settings:';

/**
 * Reads this page has made, per viewer and track, so a track is asked about once however many
 * stages play it — including two stages booting it at the same moment, which share one request.
 *
 * Only an answer the server STATED is kept (a value, or an absence); a failure is a fact about that
 * moment, and the next read asks again. Handed only to a read made as the same viewer and within the
 * same configuration identity epoch, which both identity edges advance — so neither an answer nor a
 * request still out is ever handed to somebody else, including an embed whose host token arrives or
 * changes and re-reads before that edge fires. A save drops its track's entry.
 * @type {Map<string, { epoch: number, promise: Promise<{outcome: string, value: object|null}> }>}
 */
const reads = new Map();

/** Keyed by the viewer the request is made AS — see `viewerCacheKey` — not the latch's bucket below,
 *  which names every embed viewer `anon`: one host token's answer must not reach the next one's read. */
function readKey(auth, trackId) {
  return `${viewerCacheKey(auth)}:${trackId}`;
}

/**
 * WHOSE nothing this is.
 *
 * The latch below remembers "this track has no settings row" so a collection of tracks nobody has
 * touched does not 404 once per track on every screen. It is per-ACCOUNT knowledge and it used to be
 * stored without saying so: one account's 404 for a track short-circuited the NEXT account's read of
 * the same track into an authoritative "you have nothing saved" — which is exactly the answer the
 * wholesale-write guard treats as safe to overwrite. The second person's real settings were then
 * replaced by defaults without a single request being made.
 *
 * A uid when the session can name one; otherwise the single bucket anonymous and host-token viewers
 * share, which the identity edges clear outright via {@link resetMissingTrackUserSettings}.
 */
function viewerLatchKey(userId) {
  return userId ? `uid:${userId}` : 'anon';
}

function getMissingSettingsStorageKey(viewerKey, trackId) {
  return `${MISSING_TRACK_SETTINGS_PREFIX}${viewerKey}:${trackId}`;
}

function isKnownMissingTrackSettings(viewerKey, trackId) {
  if (!trackId || typeof window === 'undefined' || !window.sessionStorage) {
    return false;
  }

  try {
    return window.sessionStorage.getItem(getMissingSettingsStorageKey(viewerKey, trackId)) === '1';
  } catch {
    return false;
  }
}

function markMissingTrackSettings(viewerKey, trackId) {
  if (!trackId || typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    window.sessionStorage.setItem(getMissingSettingsStorageKey(viewerKey, trackId), '1');
  } catch {
    // Ignore storage write failures.
  }
}

function clearMissingTrackSettings(viewerKey, trackId) {
  if (!trackId || typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }

  try {
    window.sessionStorage.removeItem(getMissingSettingsStorageKey(viewerKey, trackId));
  } catch {
    // Ignore storage write failures.
  }
}

/**
 * Drop every remembered absence, whoever it belonged to.
 *
 * Owner-scoped keys already stop one account reading another's answer, but only while the page can
 * still name both. An embedded host swapping tokens, or a sign-in the session could not attribute,
 * lands in the shared anonymous bucket — so the identity edges clear the lot rather than reason
 * about which entries are still meant for the person now here.
 */
export function resetMissingTrackUserSettings() {
  if (typeof window === 'undefined' || !window.sessionStorage) return;
  try {
    // Backwards: removing an entry renumbers the ones after it.
    for (let index = window.sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = window.sessionStorage.key(index);
      if (key && key.startsWith(MISSING_TRACK_SETTINGS_PREFIX)) window.sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore storage failures — the worst case is a stale absence, and every read that follows a
    // change of identity still asks the server unless it finds one.
  }
}

/** The blocks a track-settings body is allowed to carry. A response naming none of them is not a
 *  track-settings response, whatever its status line said. */
const SETTINGS_SECTIONS = ['sync', 'ui', 'playback', 'custom'];

/**
 * Normalizes backend track-settings responses into the frontend-owned shape.
 *
 * Backend storage currently lives inside a broader mixed user settings schema,
 * and the per-track payload may arrive wrapped like:
 *
 * ```json
 * {
 *   "trackSettings": {
 *     "trackId": "...",
 *     "settings": {
 *       "sync": { ... }
 *     }
 *   }
 * }
 * ```
 *
 * On the frontend we intentionally own a flatter runtime contract:
 *
 * ```js
 * {
 *   trackId,
 *   sync,
 *   ui,
 *   playback,
 *   custom
 * }
 * ```
 *
 * This normalization boundary is important:
 *
 * - the frontend runtime, hydration, and persistence code depend on this shape
 * - backend schema/storage details must not leak through the app
 * - changing the response shape here without an explicit migration will create
 *   backward-compatibility risks for saved track settings hydration
 *
 * If the backend schema evolves, update this function deliberately and keep the
 * normalized frontend contract stable unless there is a coordinated migration.
 *
 * @param {object|null} payload
 * @param {string|null} [trackId]
 * @returns {object|null}
 */
function normalizeTrackSettingsPayload(payload, trackId = null) {
  const directCandidate =
    payload?.trackUserSettings
    ?? payload?.trackSettings
    ?? payload?.settings
    // The GET track-settings response wraps the sync/ui/playback blocks in ownerSettings /
    // effectiveSettings envelopes; unwrap them here too (they were only taught to the nestedSettings
    // list below) so the flat contract doesn't leak envelope keys and the nested trackId is honored.
    ?? payload?.ownerSettings
    ?? payload?.effectiveSettings
    ?? payload?.data?.trackUserSettings
    ?? payload?.data?.trackSettings
    ?? payload?.data?.settings
    ?? payload?.data?.ownerSettings
    ?? payload?.data?.effectiveSettings
    ?? payload?.data
    ?? payload;

  if (!directCandidate || typeof directCandidate !== 'object' || Array.isArray(directCandidate)) {
    return null;
  }

  const resolvedTrackId =
    typeof directCandidate.trackId === 'string' && directCandidate.trackId.trim().length
      ? directCandidate.trackId.trim()
      : trackId;

  const nestedSettings = [
    directCandidate.settings,
    directCandidate.trackUserSettings?.settings,
    directCandidate.trackSettings?.settings,
    directCandidate.ownerSettings?.settings,
    directCandidate.effectiveSettings?.settings,
  ].find((candidate) => candidate && typeof candidate === 'object' && !Array.isArray(candidate)) ?? null;

  const normalizedSections = nestedSettings || directCandidate;

  const normalized = {
    ...directCandidate,
    ...normalizedSections,
    trackId: resolvedTrackId || null,
  };

  // A body that names not one settings block is not this endpoint's answer — a proxy's stub, a
  // backend on a different schema, an error page served as JSON. It used to normalize to a bare
  // `{ trackId }`, which read as a perfectly good settings payload: the read was called successful,
  // the provenance authoritative, and the next commit replaced the person's real stored sync with
  // whatever defaults and detected values happened to be in memory. Nothing recognisable, no read.
  const hasSection = SETTINGS_SECTIONS.some(
    (section) => normalized[section] && typeof normalized[section] === 'object',
  );
  return hasSection ? normalized : null;
}

/**
 * The SAVE path's reading of a response. A failed write is an error the caller must see — unlike a
 * failed read, which is a fact about what could be known and is reported in outcome terms by
 * {@link readTrackUserSettings}.
 */
async function handleSaveResponse(response, authErrorMessage, { trackId = null, promptOnAuthError = true } = {}) {
  if (response.status === 204) {
    return null;
  }

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      requestEmbeddedAuthToken();
      if (promptOnAuthError) {
        ensureLoginPrompt({ href: getLoginHref(), text: 'Log in' });
      }
      throw new Error(authErrorMessage);
    }

    let message = `${authErrorMessage} (status ${response.status})`;
    try {
      const data = await response.json();
      if (data?.message) {
        message = data.message;
      }
    } catch {
      // Ignore JSON parsing errors for non-JSON responses.
    }
    throw new Error(message);
  }

  try {
    const data = await response.json();
    return normalizeTrackSettingsPayload(data, trackId);
  } catch {
    return null;
  }
}

function buildTrackEndpoint(trackId) {
  if (!trackId || typeof trackId !== 'string') {
    throw new Error('trackId is required for track user settings requests.');
  }
  return `${BASE_ENDPOINT}/${encodeURIComponent(trackId)}`;
}

/**
 * Read one track's private per-user settings, and say what happened.
 *
 * `null` used to be the whole answer for a 204, a 404, a 401, a timeout and an unreachable server.
 * It is also what somebody who never set anything looks like — so a failed read silently became
 * "no settings", the engine auto-detected, and the next commit wrote those detected values over
 * what was really saved. The outcome is what tells those apart; see `sync/personalTrackSync.js`
 * for what is done with it.
 *
 * Anonymous users answer `unauthorized` without surfacing a login prompt.
 *
 * @param {string} trackId
 * @param {{ promptOnAuthError?: boolean }} [options]
 * @returns {Promise<{outcome: string, value: object|null}>}
 */
export async function readTrackUserSettings(trackId, { promptOnAuthError = false } = {}) {
  // Who is asking is settled BEFORE the remembered-absence check, because that memory belongs to an
  // account: reading it first is how one person's 404 became another person's authoritative "you
  // have nothing saved". The handshake is resolved once per session, so this costs nothing after the
  // first read — and the request is sent even without a bearer, since the session cookie
  // authenticates it on its own and an anonymous visitor is answered with a 401, prompt-free.
  let auth;
  try {
    auth = await resolveViewerAuth();
  } catch {
    // Auth that will not resolve is not a fact about the settings — the request still goes, on the
    // session cookie alone, and the server answers.
  }
  const viewerKey = viewerLatchKey(auth?.userId ?? null);

  // Already proven absent for THIS account this session — an authoritative nothing, not a failure.
  if (isKnownMissingTrackSettings(viewerKey, trackId)) {
    return { outcome: READ_OUTCOME.ABSENT, value: null };
  }

  const key = readKey(auth, trackId);
  const held = reads.get(key);
  if (held && isConfigurationEpochCurrent(held.epoch)) return held.promise;

  const entry = {
    epoch: configurationEpoch(),
    promise: requestTrackUserSettings(trackId, auth, viewerKey, promptOnAuthError),
  };
  reads.set(key, entry);
  // Only if it is still OURS — a save or a change of person may have replaced it meanwhile.
  const forget = () => {
    if (reads.get(key) === entry) reads.delete(key);
  };
  entry.promise.then(({ outcome }) => {
    if (outcome !== READ_OUTCOME.NETWORK && outcome !== READ_OUTCOME.ABSENT) forget();
  }, forget);
  return entry.promise;
}

async function requestTrackUserSettings(trackId, auth, viewerKey, promptOnAuthError) {
  let response;
  try {
    response = await fetchJsonFromApi(buildTrackEndpoint(trackId), {
      method: 'GET',
      authToken: auth?.authToken ?? null,
    });
  } catch (error) {
    const outcome = error?.name === 'AbortError' ? READ_OUTCOME.ABORTED : READ_OUTCOME.UNAVAILABLE;
    return { outcome, value: null };
  }

  if (response.status === 204) return { outcome: READ_OUTCOME.ABSENT, value: null };

  if (!response.ok) {
    if (response.status === 404) {
      markMissingTrackSettings(viewerKey, trackId);
      return { outcome: READ_OUTCOME.ABSENT, value: null };
    }
    if (response.status === 401 || response.status === 403) {
      requestEmbeddedAuthToken();
      if (promptOnAuthError) ensureLoginPrompt({ href: getLoginHref(), text: 'Log in' });
      return { outcome: READ_OUTCOME.UNAUTHORIZED, value: null };
    }
    return { outcome: READ_OUTCOME.UNAVAILABLE, value: null };
  }

  let data;
  try {
    data = await response.json();
  } catch {
    return { outcome: READ_OUTCOME.INVALID_RESPONSE, value: null };
  }

  const normalized = normalizeTrackSettingsPayload(data, trackId);
  // A 200 carrying nothing this endpoint could have meant is a broken answer, not an empty one —
  // the endpoint says "nothing saved" with a 404 or a 204. Calling it absent would hand the commit
  // path a licence to overwrite.
  if (!normalized) return { outcome: READ_OUTCOME.INVALID_RESPONSE, value: null };
  clearMissingTrackSettings(viewerKey, trackId);
  return { outcome: READ_OUTCOME.NETWORK, value: normalized };
}

/**
 * Persists private per-user settings for one track.
 *
 * @param {string} trackId
 * @param {object} payload
 * @param {{ promptOnAuthError?: boolean }} [options]
 * @returns {Promise<object|null>}
 */
export async function saveTrackUserSettings(trackId, payload, { promptOnAuthError = true } = {}) {
  // A file on this machine has no settings row on the platform. Its BPM, loop and warp edits stay
  // in the session; sending them would put the entry's id on the wire and create a row for nothing.
  if (isLocalEntryId(trackId)) return null;
  if (!payload || typeof payload !== 'object') {
    throw new Error('saveTrackUserSettings requires a payload object.');
  }

  const auth = await resolveViewerAuth();
  const { authToken, userId } = auth;
  let saved;
  try {
    const response = await fetchJsonFromApi(buildTrackEndpoint(trackId), {
      method: 'PUT',
      authToken,
      body: payload,
    });
    saved = await handleSaveResponse(response, 'Authentication required to save track settings', {
      trackId,
      promptOnAuthError,
    });
  } finally {
    // Once the write is DONE, and whether or not it landed — a read issued while it was out can
    // come back with what was stored before it, and a failure on the way back may still have been
    // applied. The next read asks.
    reads.delete(readKey(auth, trackId));
  }

  // There is a row now, so the remembered absence is wrong for this account from here on.
  clearMissingTrackSettings(viewerLatchKey(userId ?? null), trackId);
  return saved;
}
